import { useEffect, useRef, useState } from "react";
import type { ModeConfig } from "../state/modeConfig";
import type { usePlayerState } from "../hooks/usePlayerState";
import { useGameSession } from "../session/useGameSession";
import {
  chunkSequence,
  generateMixedSequence,
  partialAccuracy,
} from "../logic/sequences";
import { TopBar } from "../components/TopBar";
import { Instructions } from "../screens/Instructions";
import { Results } from "../screens/Results";
import { FeedbackOverlay } from "../components/FeedbackOverlay";
import { CountdownOverlay } from "../components/CountdownOverlay";
import { MODE_COUNTDOWN_MS } from "../state/modeConfig";
import { NumberPad } from "../components/NumberPad";
import { LetterPad } from "../components/LetterPad";
import { useTranslation } from "../i18n/useTranslation";

const RAW_SHOW_MS = 3000;
const CHUNKED_SHOW_MS = 5000;

const CHUNK_COLORS = [
  "var(--color-cyan)",
  "var(--color-purple)",
  "var(--color-amber)",
  "var(--color-green)",
];

const TIP_KEYS = [
  "chunking.tip.0",
  "chunking.tip.1",
  "chunking.tip.2",
  "chunking.tip.3",
  "chunking.tip.4",
  "chunking.tip.5",
];

type SubPhase = "showRaw" | "showChunked" | "recall";

interface ChunkingProps {
  config: ModeConfig;
  player: ReturnType<typeof usePlayerState>;
  onExit: () => void;
}

export function ChunkingChallenge({ config, player, onExit }: ChunkingProps) {
  const game = useGameSession(config.modeId, player);
  const { session } = game;
  const { t } = useTranslation();

  const [sequence, setSequence] = useState<string[]>([]);
  const [chunks, setChunks] = useState<string[][]>([]);
  const [useDigits, setUseDigits] = useState(true);
  const [tipKey, setTipKey] = useState(TIP_KEYS[0]);
  const [subPhase, setSubPhase] = useState<SubPhase>("showRaw");
  const [playerInput, setPlayerInput] = useState<string[]>([]);
  const recallSecondsRef = useRef(0);
  const [, force] = useState(0);

  const lastRoundRef = useRef(-1);
  useEffect(() => {
    if (session.phase.kind !== "playing") return;
    if (lastRoundRef.current === session.roundNum) return;
    lastRoundRef.current = session.roundNum;
    startRound();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.phase, session.roundNum]);

  function startRound(): void {
    const seqLen = 12 + session.difficulty * 2;
    const cs = Math.max(2, 4 - Math.floor(session.difficulty / 3));
    const digits = Math.random() < 2 / 3; // exactly 2/3 digits, matching desktop's choice([T, T, F])
    const seq = generateMixedSequence(seqLen, digits);
    setUseDigits(digits);
    setSequence(seq);
    setChunks(chunkSequence(seq, cs));
    setPlayerInput([]);
    setTipKey(TIP_KEYS[Math.floor(Math.random() * TIP_KEYS.length)]);
    setSubPhase("showRaw");
    recallSecondsRef.current = Math.max(20, 40 - session.difficulty * 2);
  }

  useEffect(() => {
    if (subPhase !== "showRaw") return;
    const id = window.setTimeout(() => setSubPhase("showChunked"), RAW_SHOW_MS);
    return () => window.clearTimeout(id);
  }, [subPhase]);

  useEffect(() => {
    if (subPhase !== "showChunked") return;
    const id = window.setTimeout(() => setSubPhase("recall"), CHUNKED_SHOW_MS);
    return () => window.clearTimeout(id);
  }, [subPhase]);

  useEffect(() => {
    if (subPhase !== "recall") return;
    const id = window.setInterval(() => {
      recallSecondsRef.current -= 1;
      force((x) => x + 1);
      if (recallSecondsRef.current <= 0) {
        window.clearInterval(id);
        submit();
      }
    }, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subPhase]);

  function chunkColorForIndex(i: number): string {
    let pos = 0;
    for (let ci = 0; ci < chunks.length; ci++) {
      const len = chunks[ci].length;
      if (i >= pos && i < pos + len) {
        return CHUNK_COLORS[ci % CHUNK_COLORS.length];
      }
      pos += len;
    }
    return "var(--color-fg-2)";
  }

  function handleInput(ch: string): void {
    if (subPhase !== "recall" || playerInput.length >= sequence.length) return;
    setPlayerInput([...playerInput, ch]);
  }
  function handleBackspace(): void {
    if (subPhase !== "recall" || playerInput.length === 0) return;
    setPlayerInput(playerInput.slice(0, -1));
  }
  function submit(): void {
    if (subPhase !== "recall") return;
    const acc = partialAccuracy(playerInput, sequence);
    game.submitAnswer(acc >= 0.7);
  }

  if (session.phase.kind === "instructions") {
    const length = 12 + session.difficulty * 2;
    const size = Math.max(2, 4 - Math.floor(session.difficulty / 3));
    return (
      <Instructions
        config={config}
        difficulty={session.difficulty}
        highScore={session.playerState.modeHighScores[config.modeId] ?? 0}
        instructions={[
          t("chunking.instr.study", { n: length }),
          t("chunking.instr.chunks"),
          t("chunking.instr.recall"),
          t("chunking.instr.why"),
        ]}
        difficultyDescription={t("chunking.diffDesc", { length, size })}
        onStart={game.startPlaying}
        onBack={onExit}
        onDifficultyChange={game.setDifficulty}
      />
    );
  }
  if (session.phase.kind === "results") {
    return (
      <Results
        modeName={t(`mode.${config.modeId}.name`)}
        result={session.sessionResult}
        level={session.playerState.level}
        xpProgressFrac={session.playerState.xpProgressFrac}
        onContinue={onExit}
      />
    );
  }

  return (
    <main className="min-h-screen flex flex-col relative">
      <TopBar
        modeName={t(`mode.${config.modeId}.name`)}
        score={session.score}
        streak={session.streak.current}
        roundNumber={Math.min(session.roundNum + 1, session.maxRounds)}
        totalRounds={session.maxRounds}
      />

      <section className="flex-1 flex flex-col items-center justify-center px-5 gap-5 relative">
        {subPhase === "showRaw" && (
          <>
            <span className="font-mono text-xs tracking-[0.18em] uppercase text-cyan">
              {t("chunking.memorize")}
            </span>
            <p className="text-xl font-bold font-mono text-fg-0 leading-relaxed text-center">
              {sequence.join(" ")}
            </p>
          </>
        )}

        {subPhase === "showChunked" && (
          <>
            <span className="font-mono text-xs tracking-[0.18em] uppercase text-green">
              {t("chunking.seeChunks")}
            </span>
            <div className="flex flex-wrap gap-3 justify-center max-w-xl">
              {chunks.map((chunk, ci) => (
                <div
                  key={ci}
                  className="flex gap-0.5 px-2 py-1 rounded"
                  style={{
                    background: `color-mix(in oklab, ${CHUNK_COLORS[ci % CHUNK_COLORS.length]} 15%, transparent)`,
                  }}
                >
                  {chunk.map((c, i) => (
                    <span
                      key={i}
                      className="text-lg font-bold font-mono px-1"
                      style={{ color: CHUNK_COLORS[ci % CHUNK_COLORS.length] }}
                    >
                      {c}
                    </span>
                  ))}
                </div>
              ))}
            </div>
            <p className="text-amber text-xs text-center max-w-md">{t(tipKey)}</p>
          </>
        )}

        {subPhase === "recall" && (
          <div className="w-full flex flex-col items-center gap-4">
            <span className="font-mono text-xs tracking-[0.18em] uppercase text-fg-2">
              {t("chunking.enterFull")}
            </span>
            <div className="w-full overflow-x-auto">
              <div className="flex gap-1 justify-center min-w-min">
                {sequence.map((_, i) => {
                  const filled = i < playerInput.length;
                  const active = i === playerInput.length;
                  return (
                    <div
                      key={i}
                      className="w-9 h-11 rounded bg-bg-2 border-2 flex items-center justify-center text-base font-bold font-mono"
                      style={{
                        borderColor: active ? chunkColorForIndex(i) : "var(--color-line)",
                        color: filled ? "var(--color-fg-0)" : "var(--color-fg-3)",
                      }}
                    >
                      {filled ? playerInput[i] : ""}
                    </div>
                  );
                })}
              </div>
            </div>
            <span className="text-fg-3 text-xs">
              {playerInput.length} / {sequence.length}
              {recallSecondsRef.current <= 5 && (
                <span className="text-red ml-3">
                  {t("common.hurry", { seconds: recallSecondsRef.current })}
                </span>
              )}
            </span>
            {useDigits ? (
              <NumberPad
                onDigit={(d) => handleInput(String(d))}
                onBackspace={handleBackspace}
                onSubmit={submit}
                submitDisabled={playerInput.length === 0}
              />
            ) : (
              <LetterPad
                onLetter={handleInput}
                onBackspace={handleBackspace}
                onSubmit={submit}
                submitDisabled={playerInput.length === 0}
              />
            )}
          </div>
        )}

        {session.phase.kind === "countdown" && (
          <CountdownOverlay
            durationMs={MODE_COUNTDOWN_MS[config.modeId] ?? 0}
            onSkip={game.finishCountdown}
          />
        )}

        {session.phase.kind === "feedback" && (
          <FeedbackOverlay correct={session.phase.correct} />
        )}
      </section>
    </main>
  );
}
